import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { builderSelectedOptionsSelector } from '../../../../../redux/selectors/builder';
import {
  setActiveRetentionUnit,
  setCustomTimeBracketAction,
} from '../../../../../redux/slices/oldBuilder';
import {
  getCapitalizedTimeUnitText,
  getTimeUnitText,
} from '../../../../../utils';
import { assertCondition } from '../../../../../utils/types';
import { QueryType, TimeUnit } from '../../../../../utils/apollo/graphqlSchema';
import { RETENTION_BASIC_VIEW_KEYS } from '../../../../../utils/builder';
import * as BuilderUI from '../../../../../components/Builder/BuilderUI';
import KubitContextualMenu from '../../../../../components/Dropdown/ContextualMenu';
import ContextualMenuOption from '../../../../../components/Dropdown/ContextualMenu/ContextualMenuOption';
import TimeBrackets from './TimeBrackets';
import { SingleSelectLabel } from '../../../../../components/Dropdown/SingleSelect';

const MenuContainer = styled.div`
  flex: 0 0 auto;
`;

const RetentionUnit = () => {
  const dispatch = useAppDispatch();

  const availableRetentionUnits = useAppSelector(
    ({ builder }) => builder.availableRetentionUnits,
  );
  const retention = useAppSelector(({ builder }) => builder.retention);
  const { activeRetentionUnit, customTimeBrackets } = retention;

  const isCustomSelected = customTimeBrackets?.length > 0;

  const selectedOptions = useAppSelector((state) =>
    builderSelectedOptionsSelector(state),
  );

  assertCondition(selectedOptions?.type === QueryType.Retention);

  const setRetentionUnit = useCallback(
    (newTimeUnit: TimeUnit) => {
      dispatch(
        setActiveRetentionUnit({
          newTimeUnit,
        }),
      );
    },
    [dispatch],
  );

  const handleSelectUnitOption = useCallback(
    (unit: TimeUnit) => {
      setRetentionUnit(unit);

      if (isCustomSelected) {
        // Reset custom brackets
        dispatch(setCustomTimeBracketAction(undefined));
      }
    },
    [dispatch, isCustomSelected, setRetentionUnit],
  );

  const handleSubmitCustomBrackets = useCallback(
    (dismissMenu: (ev?: unknown, dismissAll?: boolean) => void) =>
      (event: { type: TimeUnit; data: number[] }) => {
        setRetentionUnit(event.type);
        dispatch(setCustomTimeBracketAction({ ...event }));

        dismissMenu(undefined, true);
      },
    [dispatch, setRetentionUnit],
  );

  const { retentionView, cohortUnit } = selectedOptions;

  const getCustomUnitValues = useCallback(
    (unit: TimeUnit) => {
      if (unit === activeRetentionUnit) {
        return customTimeBrackets || [];
      }
      return [];
    },
    [activeRetentionUnit, customTimeBrackets],
  );

  const items: IContextualMenuItem[] = useMemo(() => {
    const visibleRetentionUnits =
      availableRetentionUnits?.find((aru) => aru.cohortUnit === cohortUnit)
        ?.retentionUnits ?? [];

    const result: IContextualMenuItem[] = visibleRetentionUnits.map((unit) => ({
      key: `retention-unit-${unit}`,
      onRenderContent: () => (
        <ContextualMenuOption
          label={getCapitalizedTimeUnitText(unit).adverb}
          onClick={() => handleSelectUnitOption(unit)}
          isActive={!isCustomSelected && unit === activeRetentionUnit}
        />
      ),
    }));

    if (RETENTION_BASIC_VIEW_KEYS.includes(retentionView)) {
      result.push(
        {
          key: 'retention-unit-divider',
          itemType: ContextualMenuItemType.Divider,
        },
        {
          key: 'retention-unit-custom',
          onRenderContent: () => (
            <ContextualMenuOption
              label={'Custom'}
              hasSubMenus={true}
              isActive={isCustomSelected}
            />
          ),
          subMenuProps: {
            items: visibleRetentionUnits
              .filter((unit) => unit !== TimeUnit.AllTime)
              .map((unit, index) => ({
                key: `retention-unit-custom-${unit}`,
                onRenderContent: () => (
                  <ContextualMenuOption
                    label={`By ${getTimeUnitText(unit).singular}`}
                    hasSubMenus={true}
                  />
                ),
                subMenuProps: {
                  items: [
                    {
                      key: `retention-unit-custom-${unit}-${index}`,
                      onRender: (_, dismissMenu) => (
                        <TimeBrackets
                          type={unit}
                          values={getCustomUnitValues(unit)}
                          onApply={handleSubmitCustomBrackets(dismissMenu)}
                        />
                      ),
                    },
                  ],
                },
              })),
          },
        },
      );
    }

    return result;
  }, [
    activeRetentionUnit,
    availableRetentionUnits,
    cohortUnit,
    getCustomUnitValues,
    handleSelectUnitOption,
    handleSubmitCustomBrackets,
    isCustomSelected,
    retentionView,
  ]);

  useEffect(() => {
    // when currentCohortUnit updates, ensure that currentRetentionUnit is in the allowed map of units
    if (availableRetentionUnits) {
      const retentionUnits = availableRetentionUnits.find(
        (item) => item.cohortUnit === cohortUnit,
      )?.retentionUnits;

      if (retentionUnits && !retentionUnits.includes(cohortUnit)) {
        setRetentionUnit(retentionUnits[0]);
      }
    }
  }, [availableRetentionUnits, cohortUnit, setRetentionUnit]);

  return (
    <BuilderUI.BuilderFieldContainer>
      <BuilderUI.BuilderFieldLabel>retained</BuilderUI.BuilderFieldLabel>
      <MenuContainer>
        <KubitContextualMenu
          items={items}
          trigger={
            <SingleSelectLabel isAlwaysActive={true}>
              {customTimeBrackets?.length > 0
                ? 'Custom'
                : getCapitalizedTimeUnitText(activeRetentionUnit).adverb}
            </SingleSelectLabel>
          }
          style={{
            root: {
              minWidth: '160px',
            },
          }}
        />
      </MenuContainer>
    </BuilderUI.BuilderFieldContainer>
  );
};

export default RetentionUnit;
